






import { Component, Prop, Vue} from 'vue-property-decorator';
import { User } from '../models/User';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import FeatureReductionCluster from '@arcgis/core/layers/support/FeatureReductionCluster';
import Compass from '@arcgis/core/widgets/Compass';
import '@arcgis/core/assets/esri/themes/light/main.css';


@Component
export default class EsriMap extends Vue {

    get leftDrawerOpen(): boolean {
        return this.$store.state.leftDrawerOpen;
    }

    get map() {
        return this.$store.state.map;
    }

    get mapView() {
        return this.$store.state.mapView;
    }

    get layers() {
        return this.$store.state.layers;
    }

    get user(): User | null {
        return this.$store.state.user;
    }

    get audioRootUrl() {
        return this.$store.getters.audioRootUrl;
    }

    public createMap() {
        let activeBasemap = 'topo-vector';
        this.$store.state.basemaps.forEach((item: any, index: number) => {
            if (item.active === true) {
                activeBasemap = item.url;
            }
        });

        const sketchLayer: __esri.GraphicsLayer = new GraphicsLayer();
        this.$store.state.sketchLayer = sketchLayer;

        const eMap: __esri.Map = new Map({
            basemap: activeBasemap,
            layers: [sketchLayer],
        });
        this.$store.state.map = eMap;

        let approveAction;
        let rejectAction;
        let editAction;
        let deleteAction;

        // IMPROVE THIS: we need to check if at the /admin url -
        // The user object is stored in local storage on the same domain so the public side will have these actions if logged in
        if (window.location.href.indexOf('admin') !== -1 && this.user && this.user.role === 'admin') {
            approveAction = {
                title: 'Approve',
                id: 'approve-this',
                className: 'esri-icon-check-mark',
            };
            rejectAction = {
                title: 'Unapprove',
                id: 'reject-this',
                className: 'esri-icon-close',
            };
        }
        if (window.location.href.indexOf('admin') !== -1 && this.user && (this.user.role === 'admin' || this.user.role === 'contributor')) {
            editAction = {
                title: 'Edit',
                id: 'edit-this',
                className: 'esri-icon-edit',
            };
            deleteAction = {
                title: 'Delete',
                id: 'delete-this',
                className: 'esri-icon-trash',
            };
        }

        const view: __esri.MapView = new MapView({
            container: 'viewDiv',
            map: eMap,
            center: [-63.5268027, 44.982321],
            zoom: 8,
            popup: {
                // @ts-ignore
                actions: [approveAction, rejectAction, editAction, deleteAction], // remove zoom-to
                dockEnabled: true, // dock handled in vue components
                dockOptions: {
                    buttonEnabled: false,
                },
            },
        });
        this.$store.state.mapView = view;

        const compass: __esri.Compass = new Compass({
            view,
        });
        view.ui.add(compass, 'bottom-right');
        if (this.$q.platform.is.mobile) {
            view.ui.move(['zoom'], 'bottom-left');
        }

        this.createLayers();
    }

    public createLayers() {
        const popFunc = (feature: any) => {
            const div = document.createElement('div');
            let title = '<div class="text-h6 text-weight-bolder" style="width:80%;">' + feature.graphic.attributes.SmithName + '</div>';
            if (feature.graphic.attributes.SmithName2 && feature.graphic.attributes.SmithName2.length > 0) {
                title = '<div class="text-h6 text-weight-bolder" style="width:80%;">' + feature.graphic.attributes.SmithName + ' / ' + feature.graphic.attributes.SmithName2 + '</div>';
            }
            const domain: any = {
                PACIF: 'Transcriptions of Father Pacifique Guide to Micmac Place Names',
                PIERS: 'Harry Piers unpublished notes',
                RAND: 'Silas Rand English to Micmac Dictionary',
                OTHER: 'Other',
            };

            const soundFile = (feature.graphic.attributes.AudioLink ? '<div class="q-pt-sm">Audio</div><audio id="audioElement"><source src="' + this.audioRootUrl +
                feature.graphic.attributes.AudioLink + '" type="audio/mpeg">No support for audio</audio><div class=""><button id="playButton" onclick="this.style.backgroundColor=\'lightblue\'; audioElement = document.getElementById(\'audioElement\'); audioElement.play(); audioElement.onended = function(e) { document.getElementById(\'playButton\').style.backgroundColor=\'\';}" style="border-radius: 10px"><i aria-hidden="true" class="material-icons q-icon" style="font-size: 36px;">volume_up</i></button></div>' : '');
            let mediaRow = '<div class="row"><div class="col-3">' + soundFile + '</div>';
            if (feature.graphic.attributes.Video) {
                for (const video of feature.graphic.attributes.Video.split('|')) {
                    mediaRow += '<div class="col-3"><div class="q-pt-sm">Video</div><a href="' + video +
                        '" target="_blank"><button style="border-radius: 10px"><i aria-hidden="true" class="material-icons q-icon" style="font-size: 36px;">ondemand_video</i></button></div>';
                }
            }
            mediaRow += '</div>';
            const comments = (feature.graphic.attributes.Comment ?
                '<div class="q-pt-sm">Additional Comments</div><div class="text-weight-bold q-pl-md">' + feature.graphic.attributes.Comment + '</div>' : '');
            let sources = '';
            if (feature.graphic.attributes.name1 || feature.graphic.attributes.meaning1 || feature.graphic.attributes.source_detail1) {
                sources += '<div class="q-pt-sm">Source(s)</div>';
                if (feature.graphic.attributes.name1 && feature.graphic.attributes.name1.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.name1 + '</div>';
                }
                if (feature.graphic.attributes.meaning1 && feature.graphic.attributes.meaning1.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.meaning1 + '</div>';
                }
                if (feature.graphic.attributes.source1 && feature.graphic.attributes.source1.length > 0) {
                    sources += '<div class="text-italic">' + domain[feature.graphic.attributes.source1] + '</div>';
                }
                if (feature.graphic.attributes.source_detail1 && feature.graphic.attributes.source_detail1.length > 0) {
                    sources += '<div class="text-italic">' + feature.graphic.attributes.source_detail1 + '</div>';
                }
            }
            if (feature.graphic.attributes.name2 || feature.graphic.attributes.meaning2 || feature.graphic.attributes.source_detail2) {
                sources += '<div class="q-pt-sm"></div>';
                if (feature.graphic.attributes.name2 && feature.graphic.attributes.name2.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.name2 + '</div>';
                }
                if (feature.graphic.attributes.meaning2 && feature.graphic.attributes.meaning2.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.meaning2 + '</div>';
                }
                if (feature.graphic.attributes.source2 && feature.graphic.attributes.source2.length > 0) {
                    sources += '<div class="text-italic">' + domain[feature.graphic.attributes.source2] + '</div>';
                }
                if (feature.graphic.attributes.source_detail2 && feature.graphic.attributes.source_detail2.length > 0) {
                    sources += '<div class="text-italic">' + feature.graphic.attributes.source_detail2 + '</div>';
                }
            }
            if (feature.graphic.attributes.name3 || feature.graphic.attributes.meaning3 || feature.graphic.attributes.source_detail3) {
                sources += '<div class="q-pt-sm"></div>';
                if (feature.graphic.attributes.name3 && feature.graphic.attributes.name3.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.name3 + '</div>';
                }
                if (feature.graphic.attributes.meaning3 && feature.graphic.attributes.meaning3.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.meaning3 + '</div>';
                }
                if (feature.graphic.attributes.source3 && feature.graphic.attributes.source3.length > 0) {
                    sources += '<div class="text-italic">' + domain[feature.graphic.attributes.source3] + '</div>';
                }
                if (feature.graphic.attributes.source_detail3 && feature.graphic.attributes.source_detail3.length > 0) {
                    sources += '<div class="text-italic">' + feature.graphic.attributes.source_detail3 + '</div>';
                }
            }
            if (feature.graphic.attributes.name4 || feature.graphic.attributes.meaning4 || feature.graphic.attributes.source_detail4) {
                sources += '<div class="q-pt-sm"></div>';
                if (feature.graphic.attributes.name4 && feature.graphic.attributes.name4.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.name4 + '</div>';
                }
                if (feature.graphic.attributes.meaning4 && feature.graphic.attributes.meaning4.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.meaning4 + '</div>';
                }
                if (feature.graphic.attributes.source4 && feature.graphic.attributes.source4.length > 0) {
                    sources += '<div class="text-italic">' + domain[feature.graphic.attributes.source4] + '</div>';
                }
                if (feature.graphic.attributes.source_detail4 && feature.graphic.attributes.source_detail4.length > 0) {
                    sources += '<div class="text-italic">' + feature.graphic.attributes.source_detail4 + '</div>';
                }
            }
            if (feature.graphic.attributes.name5 || feature.graphic.attributes.meaning5 || feature.graphic.attributes.source_detail5) {
                sources += '<div class="q-pt-sm"></div>';
                if (feature.graphic.attributes.name5 && feature.graphic.attributes.name5.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.name5 + '</div>';
                }
                if (feature.graphic.attributes.meaning5 && feature.graphic.attributes.meaning5.length > 0) {
                    sources += '<div class="">' + feature.graphic.attributes.meaning5 + '</div>';
                }
                if (feature.graphic.attributes.source5 && feature.graphic.attributes.source5.length > 0) {
                    sources += '<div class="text-italic">' + domain[feature.graphic.attributes.source5] + '</div>';
                }
                if (feature.graphic.attributes.source_detail5 && feature.graphic.attributes.source_detail5.length > 0) {
                    sources += '<div class="text-italic">' + feature.graphic.attributes.source_detail5 + '</div>';
                }
            }

            let translationText = feature.graphic.attributes.SmithProperTranslation;
            if (translationText === '') {
                translationText = 'No Translation';
            }
            div.innerHTML = title +
                '<div class="q-pt-sm">' + feature.graphic.attributes.EnglishName + ', ' + feature.graphic.attributes.County + ' County</div>' +
                '<div class="q-pt-sm">Translation</div>' +
                '<div class="text-weight-bold">' + translationText + '</div>' +
                sources +
                comments +
                mediaRow;
            return div;
        };

        const popTemplate = {
            // autocasts as new PopupTemplate()
            content: popFunc,
            outFields: ['*'],
        };

        const labelClass: __esri.LabelClass = {
            // @ts-ignore autocasts as new LabelClass()
            symbol: {
                type: 'text',
                // @ts-ignore
                haloColor: 'white',
                // @ts-ignore
                haloSize: '1px',
                // @ts-ignore
                font: {
                    size: 8,
                    weight: 'bold',
                },
            },
            minScale: 180000,
            labelPlacement: 'above-center',
            labelExpressionInfo: {
                expression: '$feature.SmithName + " : " + $feature.EnglishName',
            },
        };

        const pointRenderer: __esri.SimpleRenderer = {
            // @ts-ignore autocasts as new SimpleRenderer()
            type: 'simple',
            symbol: {
                type: 'simple-marker',
                size: 6,
                // @ts-ignore quasar red-9
                color: [119, 61, 26],
                outline: {
                    width: 0.5,
                    color: 'white',
                },
            },
        };

        const layerList: __esri.Layer[] = [];
        const clusterConfig: __esri.FeatureReductionCluster = new FeatureReductionCluster({
            clusterRadius: '100px',
            // {cluster_count} is an aggregate field containing
            // the number of features comprised by the cluster
            clusterMinSize: '24px',
            clusterMaxSize: '60px',
            labelingInfo: [
                {
                    deconflictionStrategy: 'none',
                    labelExpressionInfo: {
                        expression: 'Text($feature.cluster_count, \'#,###\')',
                    },
                    labelPlacement: 'center-center',
                    symbol: {
                        type: 'text',
                        color: 'white',
                        font: {
                            size: '12px',
                        },
                        haloSize: 0,
                        },
                },
            ],
        });

        this.layers.forEach((item: any, index: number) => {
            if (item.search === true) {
                this.$store.state.search.layer = new FeatureLayer({
                    url: item.url,
                    outFields: [
                        'SmithName',
                        'SmithName2',
                        'EnglishName',
                        'County',
                        'SmithProperTranslation',
                        'name1',
                        'meaning1',
                        'source1',
                        'source_detail1',
                        'name2',
                        'meaning2',
                        'source2',
                        'source_detail2',
                        'name3',
                        'meaning3',
                        'source3',
                        'source_detail3',
                        'name4',
                        'meaning4',
                        'source4',
                        'source_detail4',
                        'name5',
                        'meaning5',
                        'source5',
                        'source_detail5',
                        'Comment',
                        'AudioLink',
                        'Video',
                    ],
                    visible: item.active,
                    renderer: pointRenderer,
                    popupTemplate: popTemplate,
                    popupEnabled: true, // !this.leftDrawerOpen, // popups depend on leftDrawer state
                    labelingInfo: [labelClass],
                    featureReduction: clusterConfig,
                });
                layerList.unshift(this.$store.state.search.layer);
            } else if (item.unapproved === true) {
                if (window.location.href.indexOf('admin') !== -1 && this.user && (this.user.role === 'admin' || this.user.role === 'contributor')) {
                    this.$store.state.unapproved.layer = new FeatureLayer({
                        title: item.name,
                        url: item.url,
                        outFields: [
                            'SmithName',
                            'SmithName2',
                            'EnglishName',
                            'County',
                            'SmithProperTranslation',
                            'name1',
                            'meaning1',
                            'source1',
                            'source_detail1',
                            'name2',
                            'meaning2',
                            'source2',
                            'source_detail2',
                            'name3',
                            'meaning3',
                            'source3',
                            'source_detail3',
                            'name4',
                            'meaning4',
                            'source4',
                            'source_detail4',
                            'name5',
                            'meaning5',
                            'source5',
                            'source_detail5',
                            'Comment',
                            'AudioLink',
                            'Video',
                        ],
                        visible: item.active,
                        // renderer: pointRenderer,
                        popupTemplate: popTemplate,
                        popupEnabled: true, // !this.leftDrawerOpen, // popups depend on leftDrawer state
                        labelingInfo: [labelClass],
                    });
                    if (this.user.role === 'contributor') {
                        this.$store.state.unapproved.layer.definitionExpression = `entered_by = '${this.user.name}'`;
                    }
                    layerList.unshift(this.$store.state.unapproved.layer);
                }
            } else if (item.type === 'FeatureLayer') {
                const fLayer: __esri.FeatureLayer = new FeatureLayer({
                    url: item.url,
                    visible: item.active,
                });
                if (item.hasOwnProperty('minScale')) {
                    fLayer.minScale = item.minScale ;
                }
                layerList.unshift(fLayer);
            } else if (item.type === 'MapImageLayer') {
                const miLayer: __esri.MapImageLayer = new MapImageLayer({
                    url: item.url,
                    visible: item.active,
                    // sublayers: item.visibleSubLayers,
                });
                layerList.unshift(miLayer);
            }
        });

        const watchHandle = this.mapView.on('layerview-create', (event: any) => {
            watchHandle.remove();
            this.$store.dispatch('initSearch');
            this.$store.dispatch('initPopup');
            this.updateAttribution();
            this.updateCompassTooltip();
            this.setupActions();
            this.map.layers.forEach((layer: __esri.Layer) => {
                this.mapView.whenLayerView(layer).then((layerView: __esri.FeatureLayerView) => {
                    if (layer.id === this.$store.state.search.layer.id) {
                        this.$store.state.search.layerView = layerView;
                    }
                });
            });

            if (window.location.href.indexOf('admin') !== -1 && this.user) { // extra admin setup
                this.$store.dispatch('updateUnapprovedCount');
                this.$store.dispatch('updateUnapprovedData').then(() => {
                    if (this.user && this.user.role === 'admin') {
                        this.$store.dispatch('drawerToggleLeft', 'approvals');
                    }
                });
                this.map.layers.forEach((layer: __esri.Layer) => {
                    this.mapView.whenLayerView(layer).then((layerView: __esri.FeatureLayerView) => {
                        if (layer.id === this.$store.state.unapproved.layer.id) {
                            this.$store.state.unapproved.layerView = layerView;
                        }
                    });
                });
            }
        });
        this.$store.state.map.layers.addMany(layerList);
    }

    public setupActions() {
        this.mapView.popup.on('trigger-action', (event: any) => {
            const attributes = this.mapView.popup.viewModel.selectedFeature.attributes;
            if (event.action.id === 'approve-this') {
                this.$store.dispatch('approvePlacename', attributes.OBJECTID);
            } else if (event.action.id === 'reject-this') {
                this.$store.dispatch('rejectPlacename', attributes.OBJECTID);
            } else if (event.action.id === 'edit-this') {
                if (this.user && this.user.role !== 'admin' && this.user.name !== attributes.entered_by) {
                    this.$store.dispatch('notify', 'Only editable by admin or owner');
                } else if (this.user && this.user.role !== 'admin' && attributes.approved === 1) {
                    this.$store.dispatch('notify', 'Unable to edit approved place names');
                } else {
                    this.$store.dispatch('editPlacename', attributes.OBJECTID);
                    this.mapView.popup.close();
                }
            } else if (event.action.id === 'delete-this') {
                if (this.user && this.user.role !== 'admin' && this.user.name !== attributes.entered_by) {
                    this.$store.dispatch('notify', 'Permission denied');
                } else if (attributes.approved === 1) {
                    this.$store.dispatch('notify', 'Unable to delete approved place names');
                } else {
                    this.$q.dialog({
                        title: 'Confirm',
                        message: 'Are you sure you want to delete?',
                        cancel: true,
                        persistent: true,
                    }).onOk(() => {
                        this.mapView.popup.close();
                        this.$store.dispatch('deletePlacename', attributes.OBJECTID);
                    }).onCancel(() => {
                        // console.log('>>>> Cancel')
                    }).onDismiss(() => {
                        // console.log('I am triggered on both OK and Cancel')
                    });
                }
            }
        });
    }

    public updateAttribution() {
        // This needs to happen afer the map has loaded
        const poweredBy = document.querySelector('.esri-attribution__powered-by') as Element;
        poweredBy.innerHTML += ' | Built by <a href="https://www.mapdev.ca" target="_blank">MapDev</a>';
    }

    public updateCompassTooltip() {
        // This needs to happen afer the map has loaded
        const compassElement = document.querySelector('.esri-compass') as Element;
        compassElement.setAttribute('title', 'Right click+drag to change orientation');
    }

    public mounted() {
        this.createMap();
    }

    public beforeDestroy() {
        console.log('map: beforeDestroy');
    }
}

